import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { User, Users, Info, DollarSign } from 'lucide-react';
import { DownloadPdfButton } from '@/components/DownloadPdfButton';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const fmtFull = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const OPTION_META = {
  single_life: { icon: User, color: '#1B2A4A', tag: 'Highest Payment' },
  joint_100_survivor: { icon: Users, color: '#2D5F8A', tag: 'Full Survivor Protection' },
  joint_50_survivor: { icon: Users, color: '#3B8C6E', tag: 'Balanced' },
};

export const AnnuityEstimatorPage = () => {
  const [loading, setLoading] = useState(true);
  const [retireAge, setRetireAge] = useState(62);
  const [annualReturn, setAnnualReturn] = useState(6);
  const [balance, setBalance] = useState(0);
  const [currentAge, setCurrentAge] = useState(35);
  const [salary, setSalary] = useState(85000);
  const [contribRate, setContribRate] = useState(5);

  // Rates from API or fallback
  const [rates, setRates] = useState({ single_life: 0.0057, joint_100_survivor: 0.0045, joint_50_survivor: 0.0051 });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sumRes, ratesRes] = await Promise.allSettled([
        api.get('/tsp/summary'),
        api.get('/tsp/market/annuity-rates'),
      ]);
      if (sumRes.status === 'fulfilled') {
        const s = sumRes.value.data;
        setBalance(s?.balances?.total || 0);
        setSalary(s?.account?.annual_salary || 85000);
        setCurrentAge(s?.participant?.age || 35);
        setContribRate(s?.fers_match?.employee_pct || 5);
      }
      if (ratesRes.status === 'fulfilled') {
        const r = ratesRes.value.data;
        setRates({
          single_life: r.single_life || 0.0057,
          joint_100_survivor: r.joint_100_survivor || 0.0045,
          joint_50_survivor: r.joint_50_survivor || 0.0051,
        });
      }
    } catch {} finally { setLoading(false); }
  };

  // Projected balance
  const projBalance = useMemo(() => {
    const n = Math.max(retireAge - currentAge, 0);
    const r = annualReturn / 100;
    const empRate = contribRate / 100;
    const matchPct = Math.min(empRate, 0.03) + Math.min(Math.max(empRate - 0.03, 0), 0.02) * 0.5;
    const annual = salary * empRate + salary * 0.01 + salary * matchPct;
    if (r > 0 && n > 0) {
      const gf = (1 + r) ** n;
      return balance * gf + annual * ((gf - 1) / r);
    }
    return n > 0 ? balance + annual * n : balance;
  }, [balance, salary, contribRate, retireAge, currentAge, annualReturn]);

  const options = useMemo(() => [
    { type: 'single_life', label: 'Single Life Annuity', monthly: projBalance * rates.single_life, factor: rates.single_life, desc: 'Payments stop at participant death.' },
    { type: 'joint_100_survivor', label: 'Joint Life, 100% Survivor', monthly: projBalance * rates.joint_100_survivor, factor: rates.joint_100_survivor, desc: 'Survivor receives 100% of payment after participant death.' },
    { type: 'joint_50_survivor', label: 'Joint Life, 50% Survivor', monthly: projBalance * rates.joint_50_survivor, factor: rates.joint_50_survivor, desc: 'Survivor receives 50% of payment after participant death.' },
  ], [projBalance, rates]);

  if (loading) return <div className="panel p-6 animate-pulse"><div className="h-6 bg-gray-100 rounded w-48 mb-4" /><div className="h-40 bg-gray-50 rounded" /></div>;

  const inputClass = "w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] font-mono focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20";

  return (
    <div className="space-y-4" data-testid="annuity-estimator-page">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-[#1B2A4A]" style={{ fontFamily: 'IBM Plex Sans' }}>Retirement Annuity Options</h1>
        <DownloadPdfButton
          path="/exports/annuity-estimate.pdf"
          params={{ target_retirement_age: retireAge, annual_return_pct: annualReturn }}
          filename="UniFed_AnnuityEstimate.pdf"
          label="Download Annuity PDF"
          testId="download-annuity-btn"
        />
      </div>

      {/* Projected Balance */}
      <div className="panel p-5 bg-[#1B2A4A] text-white" data-testid="projected-balance-banner">
        <span className="text-[10px] text-[#8B95A5] uppercase tracking-wider">Projected Retirement Balance at Age {retireAge}</span>
        <div className="font-mono text-3xl lg:text-4xl font-bold mt-1 tracking-tight" data-testid="projected-balance-value">{fmt(projBalance)}</div>
        <p className="mt-1 text-[10px] text-[#5C6A7A]">{annualReturn}% avg return · {retireAge - currentAge} years of growth · {contribRate.toFixed(1)}% contribution rate</p>
      </div>

      {/* Inputs */}
      <div className="panel p-5" data-testid="annuity-inputs">
        <span className="overline mb-3 block">Assumptions</span>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Retirement Age</label><input type="number" min={50} max={75} value={retireAge} onChange={e => setRetireAge(parseInt(e.target.value) || 62)} className={inputClass} data-testid="annuity-retire-age" /></div>
          <div><label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Annual Return %</label>
            <div className="flex items-center gap-2"><input type="range" min={0} max={12} step={0.5} value={annualReturn} onChange={e => setAnnualReturn(parseFloat(e.target.value))} className="flex-1" /><span className="font-mono text-sm w-10 text-right">{annualReturn}%</span></div>
          </div>
          <div><label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Contribution Rate %</label><input type="number" min={0} max={100} step={0.5} value={contribRate} onChange={e => setContribRate(parseFloat(e.target.value) || 0)} className={inputClass} data-testid="annuity-contrib-rate" /></div>
          <div><label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Current Balance</label><input type="number" value={Math.round(balance)} onChange={e => setBalance(parseFloat(e.target.value) || 0)} className={inputClass} data-testid="annuity-balance" /></div>
        </div>
      </div>

      {/* Three Annuity Options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="annuity-options-grid">
        {options.map((opt) => {
          const meta = OPTION_META[opt.type];
          const Icon = meta.icon;
          return (
            <div key={opt.type} className="panel p-5 panel-hover" data-testid={`annuity-option-${opt.type}`}>
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded flex items-center justify-center" style={{ backgroundColor: meta.color }}>
                    <Icon size={16} className="text-white" />
                  </div>
                  <span className="text-xs font-semibold text-[#1B2A4A]">{opt.label}</span>
                </div>
                <span className="text-[8px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>{meta.tag}</span>
              </div>

              {/* Monthly amount */}
              <div className="mb-3">
                <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Monthly Payment</span>
                <div className="font-mono text-2xl font-bold text-[#1B2A4A] mt-0.5" data-testid={`annuity-monthly-${opt.type}`}>{fmtFull(opt.monthly)}</div>
                <span className="text-[10px] text-[#9CA3AF] font-mono">{fmtFull(opt.monthly * 12)}/year</span>
              </div>

              {/* Factor */}
              <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded px-3 py-2 mb-3">
                <div className="flex justify-between text-xs">
                  <span className="text-[#6B7280]">Rate Factor</span>
                  <span className="font-mono font-medium text-[#1B2A4A]">{opt.factor}</span>
                </div>
                <p className="text-[9px] text-[#9CA3AF] font-mono mt-1">{fmt(projBalance)} × {opt.factor} = {fmtFull(opt.monthly)}</p>
              </div>

              {/* Description */}
              <p className="text-xs text-[#6B7280] leading-relaxed" data-testid={`annuity-desc-${opt.type}`}>{opt.desc}</p>
            </div>
          );
        })}
      </div>

      {/* MetLife note */}
      <div className="bg-blue-50 border border-blue-200 rounded px-4 py-3" data-testid="annuity-metlife-note">
        <div className="flex items-start gap-2">
          <Info size={13} className="text-blue-600 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-700 leading-relaxed">
            Annuity provided through MetLife. Actual rates vary by age and prevailing interest rates at time of purchase. The factors shown are approximations for planning purposes only.
          </p>
        </div>
      </div>

      {/* Comparison note */}
      <div className="panel p-5" data-testid="annuity-comparison">
        <span className="overline mb-3 block">Annuity vs. Withdrawal Comparison</span>
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3">
            <span className="text-[10px] text-[#9CA3AF] block mb-1">4% Withdrawal Rule</span>
            <span className="font-mono text-lg font-semibold text-[#1B2A4A]">{fmtFull(projBalance * 0.04 / 12)}/mo</span>
            <p className="text-[10px] text-[#9CA3AF] mt-1">Flexible, balance remains invested. Not guaranteed for life.</p>
          </div>
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3">
            <span className="text-[10px] text-[#9CA3AF] block mb-1">Single Life Annuity</span>
            <span className="font-mono text-lg font-semibold text-[#1B2A4A]">{fmtFull(options[0].monthly)}/mo</span>
            <p className="text-[10px] text-[#9CA3AF] mt-1">Guaranteed for life. No market risk. Balance is forfeited.</p>
          </div>
        </div>
      </div>

      <p className="text-[10px] text-[#9CA3AF] text-center">
        Projections are estimates only. Not financial advice. Consult a qualified advisor.
      </p>
    </div>
  );
};
